/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useEffect} from 'react';
import {useQuery} from 'react-query';
import {sub} from 'date-fns';
import {convertReportsToDetails} from '@ion/core/src/entities/Devices';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import UserPreferences from '@ion/core/src/preferences/UserPreferences';
import {getDeviceReport, loadDevicesBySmartSearch} from '@ion/core/src/services/Connect';
import {getUserTimeZone} from '@ion/core/src/timezone/timezone';
import {GenericObject, MESSAGE_TYPES} from '@ion/core/src/types/entities';
import {SensorUnitType} from '@ion/core/src/types/general';
import {Unit} from '@ion/core/src/units/units';
import {QueryKey} from '../config/QueryKeys';
import {createQueryResult, DurationResult, getNumberDecimal, getStaleTime} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export interface useDeviceSensorsProps {
  deviceId?: string;
  enabled?: boolean;
  msgType?: MESSAGE_TYPES;
  staleTime?: number | DurationResult;
  systemOfUnits?: Unit;
  settings?: GenericObject;
  event?: GenericObject[];
}

export type useDeviceSensorsResult = UseDefaultQueryResult & {
  results: any;
  reports: any;
};

/**
 * The useDeviceSensors hook provides the Query for fetching tag reports.
 *
 * ```typescript
 *
 * import {useDeviceSensors} from '@ion/query'
 * const {useDeviceSensors} = useDeviceSensors();
 *
 * ```
 *
 * @param   baseKey a constant passed in to identify the dependency key.
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to ensure authentication has been completed prior to
 *          issuing the request.
 * @param   staleTime Allows for setting the staleTime for cached query data,
 *          defaults to 60 minutes (CTC session timeout duration).
 * @param   deviceId device id for the sc device to query
 * @param   onSuccess containing the query results and the device types.
 */

export const useDeviceSensors = ({
  enabled = false,
  deviceId = '',
  msgType = MESSAGE_TYPES.TAG_REPORT,
  staleTime = getStaleTime(60, false, false),
  systemOfUnits
}: useDeviceSensorsProps) => {
  const query = useQuery([QueryKey.SC_DEVICE_SENSOR, deviceId], async() => {
    if (deviceId?.length > 0) {
      try {
        const reports = await getDeviceReport({
          deviceId,
          useEsn          : false,
          timeRange       : 1,
          msgType         : msgType,
          maxResults      : 1000,
          customDateRange : msgType === MESSAGE_TYPES.LOCO_MESSAGE
            ? `[${sub(new Date(), {
              days : 1
            }).toISOString()} TO ${(new Date()).toISOString()}]`
            : undefined
        });

        const {devices} = await loadDevicesBySmartSearch({
          queryString           : `(id:${deviceId})`,
          includeDeviceSettings : true
        });

        const {settings} = devices[0]?.sciDeviceSettings || {};

        if (!settings) {
          return {};
        }

        const events = reports?.results?.map?.((report: any) => {
          if (report?.locoEvent?.event) {
            const {locoEvent: {event, eventTime}} = report;
            if (event) {
              const {g, angle} = event;
              const {GForce, Tilt} = SensorUnitType;
              const shock = translate('tracking:scDeviceSensorTitle.shock');
              const tilt = translate('tracking:scDeviceSensorTitle.tilt');
              return {
                ...report,
                title : g ? shock : tilt,
                units : g ? GForce : Tilt,
                value : g || angle,
                x     : eventTime,
                y     : getNumberDecimal(g) || getNumberDecimal(angle, 0),
                eventTime,
                settings,
                event
              };
            }
          }
          return null;
        });

        const results = {
          ...convertReportsToDetails(
            reports?.results ?? [],
            {
              timeZone   : getUserTimeZone(),
              timeFormat : UserPreferences.getSystemTimeFormat(),
              systemOfUnits,
              settings
            }),
          settings,
          events
        };
        return results;
      } catch (e) {
        error('exception in fetching reports', e);
      }
    }
    return {};
  }, {staleTime: staleTime as number, enabled});

  useEffect(() => {
    if (enabled && !query.data) {
      query.refetch();
    }
  }, [enabled, query]);

  useEffect(function() {
    query.refetch();
  }, [deviceId, systemOfUnits]);

  return createQueryResult(query, {
    reports : query.data || {}
  }) as useDeviceSensorsResult;
};

export default useDeviceSensors;
